import type { GameState, LogEntry, CommandResult } from '../types/game';
import { LOCATION_DEFS } from './locations';
import { makeEntry, spacer } from '../ui/format';

interface Milestone {
  id: string;
  name: string;
  desc: string;
  check: (state: GameState) => boolean;
}

const MILESTONES: Milestone[] = [
  { id: 'first_fish', name: 'First Catch', desc: 'Catch your first fish', check: (s) => s.fishCaught >= 1 },
  { id: 'ten_fish', name: 'Amateur Angler', desc: 'Catch 10 fish', check: (s) => s.fishCaught >= 10 },
  { id: 'hundred_fish', name: 'Seasoned Fisher', desc: 'Catch 100 fish', check: (s) => s.fishCaught >= 100 },
  { id: 'thousand_fish', name: 'Master Angler', desc: 'Catch 1,000 fish', check: (s) => s.fishCaught >= 1000 },
  { id: 'catches_500', name: 'Dedicated', desc: 'Catch 500 fish', check: (s) => s.fishCaught >= 500 },
  { id: 'catches_5000', name: 'Legendary Angler', desc: 'Catch 5,000 fish', check: (s) => s.fishCaught >= 5000 },
  { id: 'first_vessel', name: 'Fleet Owner', desc: 'Buy your first vessel', check: (s) => s.vessels.length > 0 },
  { id: 'earn_100', name: 'Pocket Change', desc: 'Earn 100 total coins', check: (s) => s.totalCoinsEarned >= 100 },
  { id: 'earn_1000', name: 'Savings Account', desc: 'Earn 1,000 total coins', check: (s) => s.totalCoinsEarned >= 1000 },
  { id: 'earn_10000', name: 'Wealthy', desc: 'Earn 10,000 total coins', check: (s) => s.totalCoinsEarned >= 10000 },
  { id: 'earn_100000', name: 'Tycoon', desc: 'Earn 100,000 total coins', check: (s) => s.totalCoinsEarned >= 100000 },
  { id: 'earn_1m', name: 'Millionaire', desc: 'Earn 1,000,000 total coins', check: (s) => s.totalCoinsEarned >= 1000000 },
  { id: 'earn_10m', name: 'Billionaire', desc: 'Earn 10,000,000 total coins', check: (s) => s.totalCoinsEarned >= 10000000 },
  { id: 'five_vessels', name: 'Armada', desc: 'Own 5 or more vessels', check: (s) => s.vessels.length >= 5 },
  { id: 'ten_vessels', name: 'Fleet Admiral', desc: 'Own 10 or more vessels', check: (s) => s.vessels.length >= 10 },
  { id: 'explorer', name: 'Explorer', desc: 'Discover 3 locations', check: (s) => s.discoveredLocations.length >= 3 },
  { id: 'world_traveler', name: 'World Traveler', desc: 'Discover all locations', check: (s) => s.discoveredLocations.length >= LOCATION_DEFS.length },
  { id: 'deep_diver', name: 'Deep Diver', desc: 'Catch a fish in the Deep Ocean', check: (s) => s.discoveredLocations.includes('deep_sea') },
  { id: 'arctic_angler', name: 'Arctic Angler', desc: 'Catch a fish in Arctic Waters', check: (s) => s.discoveredLocations.includes('arctic') },
  { id: 'shark_bait', name: 'Shark Bait', desc: 'Catch a Great White Shark', check: (s) => s.inventory.some(i => i.type === 'fish' && i.fishId === 'shark') },
  { id: 'kraken_tamer', name: 'Kraken Tamer', desc: 'Catch a Baby Kraken', check: (s) => s.inventory.some(i => i.type === 'fish' && i.fishId === 'kraken') },
  { id: 'level_5', name: 'Apprentice', desc: 'Reach player level 5', check: (s) => s.playerStats.level >= 5 },
  { id: 'level_10', name: 'Journeyman', desc: 'Reach player level 10', check: (s) => s.playerStats.level >= 10 },
  { id: 'level_20', name: 'Expert', desc: 'Reach player level 20', check: (s) => s.playerStats.level >= 20 },
  { id: 'rod_5', name: 'Master Angler', desc: 'Upgrade Fishing Rod to level 5', check: (s) => (s.equipment.find(e => e.id === 'rod')?.level ?? 0) >= 5 },
  { id: 'homeowner', name: 'Homeowner', desc: 'Upgrade your home to a House', check: (s) => (s.properties.find(p => p.id === 'home')?.tier ?? 1) >= 3 },
  { id: 'landlord', name: 'Landlord', desc: 'Upgrade your home to a Mansion', check: (s) => (s.properties.find(p => p.id === 'home')?.tier ?? 1) >= 5 },
];

export function showAchievements(state: GameState): CommandResult {
  const logEntries: LogEntry[] = [
    spacer(),
    makeEntry('═══ Achievements ═══', 'system'),
  ];
  for (const m of MILESTONES) {
    const done = m.check(state);
    const marker = done ? '✓' : '○';
    logEntries.push(
      makeEntry(`  [${marker}] ${m.name} — ${m.desc}`, done ? 'success' : 'info'),
    );
  }
  return { logEntries, statePatch: {} };
}
